import clsx from "clsx";
import { FC, useState } from "react";

import { EditableField } from "components/hover-widgets/EditableField";
import { CourseSidebarOptionsMenu } from "components/menus/CourseSidebarOptionsMenu";
import type { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { ListItem } from "mds/components/ListItem";
import { BookCourseIcon, EyeNotVisibleIcon } from "mds/icons";
import { ComponentEventProperties } from "utils/amplitude";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { onTitleChanged } from "utils/store-utils";
import { courseHomeUrl } from "utils/urls";

type AdminCourseListItemProps = {
  course: CourseType;
  canEdit: boolean;
  eventPrefix?: string;
  eventProperties?: ComponentEventProperties["eventProperties"];
  optionsMenuActions: {
    onSettings: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onCopyTo: (courseId: string, courseCodeId?: string, termId?: string) => void;
    onMoveTo: (courseCodeId: string, termId: string) => void;
    onExport: () => void;
    onArchive: () => void;
    onUnarchive: () => void;
  };
};

export const AdminCourseListItem: FC<AdminCourseListItemProps> = ({
  course,
  canEdit,
  eventPrefix,
  eventProperties,
  optionsMenuActions,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { isArchived } = useIsArchived();

  return (
    <ListItem
      className="justify-between"
      eventName={`${eventPrefix}Course selected`}
      eventProperties={{ ...eventProperties, courseTitle: course.title }}
      kind="primary"
      to={courseHomeUrl(course.id)}
    >
      <div className="body-s flex w-full min-w-12 items-center justify-between gap-2">
        <div
          className={clsx("flex w-full min-w-12 items-center gap-2", {
            "text-black-tint-55": isArchived(course),
          })}
        >
          {isArchived(course) ? <EyeNotVisibleIcon /> : <BookCourseIcon />}

          <EditableField
            className="w-full min-w-12 truncate"
            isEditing={isEditing}
            label={t("glossary.course_title")}
            textSize="s"
            value={course.title}
            onSetIsEditing={setIsEditing}
            onValueChanged={(newTitle) =>
              onTitleChanged("courses", course.id, newTitle, {
                eventName: "Course - Change title",
              })
            }
          />
        </div>

        {canEdit && (
          <CourseSidebarOptionsMenu course={course} optionsMenuActions={optionsMenuActions} />
        )}
      </div>
    </ListItem>
  );
};
